/**
 * Custom hook for handling PWA install prompts
 */

import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export interface PWAInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  isInstalling: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
  hasUserDismissed: boolean;
}

const DISMISSAL_STORAGE_KEY = 'pwa-install-dismissed';
const DISMISSAL_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export function usePWAInstall() {
  const [state, setState] = useState<PWAInstallState>({
    isInstallable: false,
    isInstalled: false,
    isInstalling: false,
    installPrompt: null,
    hasUserDismissed: false,
  });

  // Check if app is already installed
  const checkIfInstalled = useCallback(() => {
    // Check if running in standalone mode (PWA is installed)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator &&
        (window.navigator as Navigator & { standalone: boolean }).standalone === true) ||
      document.referrer.includes('android-app://');

    return isStandalone;
  }, []);

  // Check if user has recently dismissed the prompt
  const checkDismissalStatus = useCallback(() => {
    const dismissed = localStorage.getItem(DISMISSAL_STORAGE_KEY);
    if (!dismissed) return false;

    const dismissedTime = parseInt(dismissed, 10);
    const now = Date.now();

    // Clear expired dismissal
    if (now - dismissedTime > DISMISSAL_DURATION) {
      localStorage.removeItem(DISMISSAL_STORAGE_KEY);
      return false;
    }

    return true;
  }, []);

  // Handle beforeinstallprompt event
  const handleBeforeInstallPrompt = useCallback(
    (event: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      event.preventDefault();

      const promptEvent = event as BeforeInstallPromptEvent;

      setState((prev) => ({
        ...prev,
        isInstallable: true,
        installPrompt: promptEvent,
        hasUserDismissed: checkDismissalStatus(),
      }));
    },
    [checkDismissalStatus]
  );

  // Handle app installed event
  const handleAppInstalled = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isInstalled: true,
      isInstallable: false,
      installPrompt: null,
    }));
  }, []);

  // Install the PWA
  const install = useCallback(async (): Promise<boolean> => {
    if (!state.installPrompt || state.isInstalling) {
      return false;
    }

    setState((prev) => ({ ...prev, isInstalling: true }));

    try {
      // Show the install prompt
      await state.installPrompt.prompt();

      // Wait for the user's response
      const choiceResult = await state.installPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        setState((prev) => ({
          ...prev,
          isInstalled: true,
          isInstallable: false,
          installPrompt: null,
          isInstalling: false,
        }));
        return true;
      } else {
        // User dismissed, record the dismissal
        localStorage.setItem(DISMISSAL_STORAGE_KEY, Date.now().toString());
        setState((prev) => ({
          ...prev,
          hasUserDismissed: true,
          isInstalling: false,
        }));
        return false;
      }
    } catch (error) {
      console.error('Error during PWA installation:', error);
      setState((prev) => ({ ...prev, isInstalling: false }));
      return false;
    }
  }, [state.installPrompt, state.isInstalling]);

  // Dismiss the install prompt manually
  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSAL_STORAGE_KEY, Date.now().toString());
    setState((prev) => ({
      ...prev,
      hasUserDismissed: true,
      isInstallable: false,
      installPrompt: null,
    }));
  }, []);

  // Check if we should show the install prompt
  const shouldShowPrompt = useCallback(() => {
    return (
      state.isInstallable &&
      !state.isInstalled &&
      !state.isInstalling &&
      !state.hasUserDismissed &&
      !checkIfInstalled()
    );
  }, [state, checkIfInstalled]);

  // Set up event listeners
  useEffect(() => {
    // Check initial installation status
    const isInstalled = checkIfInstalled();
    const hasDismissed = checkDismissalStatus();

    setState((prev) => ({
      ...prev,
      isInstalled,
      hasUserDismissed: hasDismissed,
    }));

    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [handleBeforeInstallPrompt, handleAppInstalled, checkIfInstalled, checkDismissalStatus]);

  return {
    ...state,
    install,
    dismiss,
    shouldShowPrompt,
  };
}
